"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import useAutoClose from "@/hooks/useAutoClose";
import usePost from "@/hooks/usePost";
import { useDictionary } from "@/components/DictProvider/DictProvider";

const MobileSearch = () => {
  const dict = useDictionary();
  const pathname = usePathname();
  const router = useRouter();
  const { PostMethod } = usePost();

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const {
    showPopup: isOpen,
    tooglepopup: toggleSearch,
    closepopup: closeSearch,
    ref: panelRef,
  } = useAutoClose(true);

  // Allow the mobile bottom nav (or any other component) to open this
  // popup by dispatching a `camouflage:open-search` window event, so we
  // don't need to lift the popup state into a global store.
  useEffect(() => {
    const handleOpen = () => {
      if (!isOpen) toggleSearch();
    };
    window.addEventListener("camouflage:open-search", handleOpen);
    return () =>
      window.removeEventListener("camouflage:open-search", handleOpen);
  }, [isOpen, toggleSearch]);

  // Debounced suggestion fetch - same logic as Location.tsx
  const fetchSuggestions = async (input: string) => {
    if (!input) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await PostMethod({
        method: "get",
        url: `product/searchName/${input}`,
        language: pathname.split("/")[1],
      });
      if (response?.success) {
        setSuggestions(Array.from(new Set(response.data as string[])));
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceTimeout) clearTimeout(debounceTimeout);
    setDebounceTimeout(setTimeout(() => fetchSuggestions(val), 300));
  };

  const handleSubmit = (e?: React.FormEvent, val?: string) => {
    e?.preventDefault();
    const searchQuery = val || query;
    if (searchQuery) {
      const locale = pathname.split("/")[1] || "en";
      router.push(`/${locale}/search/${encodeURIComponent(searchQuery)}`);
      closeSearch();
    }
  };

  const handleSuggestionSelect = (
    _: React.SyntheticEvent,
    value: string | null
  ) => {
    if (value) handleSubmit(undefined, value);
  };

  // Reset state when search closes
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSuggestions([]);
    }
  }, [isOpen]);

  // Reset on navigation away from search
  useEffect(() => {
    if (pathname.split("/")[2] !== "search") {
      setQuery("");
      setSuggestions([]);
    }
  }, [pathname]);

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* Search icon trigger */}
      <button
        className="cursor-pointer"
        onClick={toggleSearch}
        aria-label="Open search"
      >
        <Image
          src="/images/search.svg"
          width={24}
          height={24}
          alt="search"
        />
      </button>

      {/* Expanded search overlay */}
      <div
        className={`fixed inset-0 z-[110] transition-opacity duration-300 ease-in-out ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        } bg-own-black`}
      >
        {/* White search bar panel at top */}
        <div
          ref={panelRef}
          className={`w-full h-[80px] bg-white flex items-center px-3 gap-2 shadow-md transition-transform duration-300 ease-in-out ${
            isOpen ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          {/* Close / back button */}
          <button
            onClick={closeSearch}
            className="flex-shrink-0"
            aria-label="Close search"
          >
            <Image
              src="/images/ep_back.svg"
              width={24}
              height={24}
              alt="back"
              className="rtl:rotate-180"
            />
          </button>

          {/* Autocomplete search input */}
          <form onSubmit={handleSubmit} className="flex-1">
            <Autocomplete
              freeSolo
              inputValue={query}
              onInputChange={(_, newValue) => setQuery(newValue)}
              onChange={handleSuggestionSelect}
              options={suggestions}
              loading={loading}
              className="w-full"
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={handleInputChange}
                  autoFocus={isOpen}
                  fullWidth
                  variant="outlined"
                  placeholder={
                    dict?.productListPage?.searchQueryPlaceHolderNEW
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "44px",
                      borderRadius: "8px",
                      backgroundColor: "#F5F5F5",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.875rem !important",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.95rem !important",
                      padding: "0 !important",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress color="inherit" size={18} />
                        )}
                        {query && (
                          <InputAdornment position="end">
                            <ClearIcon
                              fontSize="small"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setQuery("");
                                setSuggestions([]);
                              }}
                            />
                          </InputAdornment>
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default MobileSearch;
